import { Field, useFormikContext } from 'formik';
import React, { FC } from 'react';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { useLocale } from '../../contexts/localeContext';
import ReaderImage from '../../images/Reader.svg';
import LockImage from '../../images/Locks.svg';
import { ListQuery } from '../../models/ListQuery';
import { Name } from '../../models/Name';
import { PaginationModel } from '../../models/Pagination';
import { validateItemName } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import Togglebox from '../togglebox/togglebox';

interface Props {
    type: "LOCK" | "READER" | "BIOMETRIC";
    dataMethod: (listParameter: ListQuery) => Promise<{
        rows: Name[];
        pagination: PaginationModel;
    }>
}

const DeviceCard: FC<Props> = ({ dataMethod, type }) => {
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const { translate } = useLocale();

    return <>
        <div className="w-full p-2 inline-flex justify-start items-center bg-white pl-6 ">
            {type === "READER" ? <img className='hardware-image' src={ReaderImage} alt="reader" /> 
            : <img className='hardware-image' src={LockImage} alt="lock" />}

            <div className="flex flex-col py-8 px-5 card-details ">
                {values.id ?
                    <div className="flex flex-col px-4"><EntityHierarchy title={values.name} tree={{ label: values.site, node: { label: values.controller, node: { label: values.door, node: { label: values.fixedAddress } } } }} /></div>
                    : null}
            </div>
            <div className="flex flex-col py-4 px-8">
                <div className="mb-1">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="name"
                            required
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.name)}:</Label>
                        {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                    </div>
                    <Field
                        type="text"
                        name="name"
                        autoFocus
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => validateItemName(value, translate)} />
                </div>

                <div className="mb-1">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="fixedAddress"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.fixedAddress)}:</Label>
                    </div>
                    <Field
                        name="fixedAddress"
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                </div>

                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="logicalAddress"
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.logicalAddress)}:</Label>
                </div>
                <Field
                    name="logicalAddress"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            <div className="flex flex-col py-4 px-8">
                <div className="mb-1">
                    <div className="flex flex-row justify-between">
                        <Label
                            required
                            htmlFor="presetId"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.preset)}:</Label>
                        {errors.peresetId ? <span className='text-red-600 text-xs'>{errors.peresetId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.preset)}
                        key={"presetId"}
                        accessor='presetId'
                        dataMethod={dataMethod}
                        searchAccessor={'trnName'}
                        onSelect={(_,item) => {
                            setFieldValue('preset', item?.label)
                            setFieldValue('presetId', item?.value)
                        }}
                        selectedItemLabel={values.preset} />
                </div>
                <div className="mb-1">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="ipAddress"
                            required
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.ipAddress)}:</Label>
                    </div>
                    <Field
                        name="ipAddress"
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                </div>
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="enabled"
                        className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.enabled)}:</Label>
                </div>
                <div className="my-0">
                    <Togglebox name="enabled" label="" />
                </div>
            </div>
        </div>
    </>;
};

export default DeviceCard;
